import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, MagnifyingGlass, Buildings, Plus, Trash, Star, ArrowsCounterClockwise } from "@phosphor-icons/react";

const COLS = ["included", "excluded"];
const COL_META = {
  included: { label: "Included (open / mgmt)", color: "bg-emerald-50", border: "border-emerald-300", chip: "bg-emerald-100 text-emerald-800", accent: "text-emerald-700" },
  excluded: { label: "Excluded", color: "bg-zinc-50", border: "border-zinc-300", chip: "bg-zinc-100 text-zinc-700", accent: "text-zinc-500" },
};

export default function AdminOpenStateMapping() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [query, setQuery] = useState("");
  const [newBody, setNewBody] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/open-state-mapping");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load mapping");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const move = async (body, category, from, to) => {
    if (from === to) return;
    setData((d) => {
      if (!d) return d;
      const next = structuredClone(d);
      const row = next.bodies.find((b) => b.counselling_body === body);
      if (!row) return d;
      row.buckets[from] = (row.buckets[from] || []).filter((c) => c !== category);
      row.buckets[to] = [...(row.buckets[to] || []), category].sort();
      return next;
    });
    const key = `${body}|${category}`;
    setSaving(key);
    try {
      await api.patch("/admin/open-state-mapping", { counselling_body: body, category, bucket: to });
      toast.success(`${category} → ${to === "included" ? "Included" : "Excluded"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      await load();
    } finally { setSaving(null); }
  };

  const addBody = async () => {
    if (!newBody) { toast.error("Pick a counselling body to add"); return; }
    try {
      await api.post("/admin/open-state-mapping/body", { counselling_body: newBody, included_categories: [] });
      toast.success(`Added ${newBody} as an open state`);
      setNewBody("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const removeBody = async (body) => {
    if (!window.confirm(`Remove ${body} from the open-state mapping? Non-domicile candidates will no longer see its seats.`)) return;
    try {
      await api.delete(`/admin/open-state-mapping/body/${encodeURIComponent(body)}`);
      toast.success(`Removed ${body}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const resetBody = async (body) => {
    if (!window.confirm(`Reset ${body} to its original default Included categories?`)) return;
    try {
      const r = await api.post(`/admin/open-state-mapping/body/${encodeURIComponent(body)}/reset`);
      toast.success(`${body} reset to defaults: ${r.data.included_categories.join(", ")}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  const bodies = useMemo(() => {
    if (!data?.bodies) return [];
    if (!query.trim()) return data.bodies;
    const q = query.toLowerCase();
    return data.bodies
      .map((b) => {
        const filtered = {};
        for (const k of COLS) filtered[k] = (b.buckets[k] || []).filter((c) => c.toLowerCase().includes(q));
        const total = COLS.reduce((n, k) => n + filtered[k].length, 0);
        if (total === 0 && !b.counselling_body.toLowerCase().includes(q)) return null;
        return { ...b, buckets: total === 0 ? b.buckets : filtered };
      })
      .filter(Boolean);
  }, [data, query]);

  const addable = data?.meta?.addable_bodies || [];

  return (
    <div className="min-h-screen bg-white" data-testid="admin-open-state-page">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/admin" className="text-xs font-bold uppercase text-zinc-500 hover:text-[#6043EF] flex items-center gap-1" data-testid="back-to-admin">
            <ArrowLeft size={12}/> Admin
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B] flex items-center gap-2">
            <Star size={22} weight="fill" className="text-[#F59E0B]"/> Open-state mapping
          </h1>
          {data?.meta && (
            <span className="text-xs font-mono font-bold text-zinc-500">{data.meta.mapped_count} open states</span>
          )}
        </div>
        <p className="text-sm text-zinc-500 mb-6 max-w-3xl leading-relaxed">
          For each open state, the <b className="text-emerald-700">Included</b> categories are the open / management / NRI seats a
          <b> non-domicile</b> candidate can access — these are the only ones shown in the predictor's CATEGORY dropdown for that body when
          "Also include open states" is on. Drag a category between columns to change it (saved instantly). This is the single source of
          truth used by both the predictor UI and the backend match.
        </p>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by body or category…"
              className="h-8 pl-8 pr-3 text-xs border border-zinc-300 rounded-md focus:outline-none focus:border-[#6043EF] w-64"
              data-testid="open-state-search"/>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <select value={newBody} onChange={(e) => setNewBody(e.target.value)} data-testid="add-open-state-select"
              className="h-8 px-2 text-xs border border-zinc-300 rounded-md focus:outline-none focus:border-[#6043EF] bg-white">
              <option value="">Add an open state…</option>
              {addable.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <button onClick={addBody} disabled={!newBody} data-testid="add-open-state-btn"
              className="h-8 px-3 text-[11px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-[#6043EF] hover:text-[#6043EF] rounded-md flex items-center gap-1 disabled:opacity-50">
              <Plus size={12} weight="bold"/> Add
            </button>
          </div>
        </div>

        {loading && <div className="p-8 text-center text-zinc-400 text-sm">Loading mapping…</div>}

        <div className="space-y-3">
          {bodies.map((row) => (
            <BodyRow key={row.counselling_body} row={row} onDragStart={setDragging}
              onDragEnd={() => setDragging(null)} dragging={dragging} onDrop={move}
              savingKey={saving} onRemove={removeBody} onReset={resetBody}/>
          ))}
          {!loading && bodies.length === 0 && (
            <div className="p-8 text-center text-zinc-400 text-sm">{query ? `No matches for "${query}".` : "No open states mapped yet — add one above."}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function BodyRow({ row, onDragStart, onDragEnd, dragging, onDrop, savingKey, onRemove, onReset }) {
  return (
    <div className="border-2 border-zinc-200 rounded-lg overflow-hidden" data-testid={`open-state-row-${row.counselling_body}`}>
      <div className="bg-zinc-900 text-white px-4 py-2 flex items-center gap-2">
        <Buildings size={14} weight="bold" className="text-amber-300"/>
        <span className="font-bold text-sm">{row.counselling_body}</span>
        <span className="text-[10px] font-mono text-zinc-400 ml-auto">
          {(row.buckets.included || []).length} included · {row.total} total
        </span>
        {row.has_default && (
          <button onClick={() => onReset(row.counselling_body)} data-testid={`reset-open-state-${row.counselling_body}`}
            className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-amber-300 flex items-center gap-1" title="Reset to original defaults">
            <ArrowsCounterClockwise size={12} weight="bold"/> Reset
          </button>
        )}
        <button onClick={() => onRemove(row.counselling_body)} data-testid={`remove-open-state-${row.counselling_body}`}
          className="text-zinc-400 hover:text-rose-400" title="Remove this open state">
          <Trash size={14} weight="bold"/>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
        {COLS.map((col) => {
          const meta = COL_META[col];
          const isDropTarget = dragging && dragging.body === row.counselling_body && dragging.from !== col;
          return (
            <div key={col}
              onDragOver={(e) => { if (isDropTarget) e.preventDefault(); }}
              onDrop={(e) => {
                e.preventDefault();
                if (!dragging || dragging.body !== row.counselling_body) return;
                onDrop(row.counselling_body, dragging.category, dragging.from, col);
              }}
              className={`min-h-[80px] p-2.5 ${meta.color} ${isDropTarget ? "ring-2 ring-inset ring-[#6043EF]" : ""}`}
              data-testid={`open-bucket-${row.counselling_body}-${col}`}>
              <div className={`text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5 ${meta.accent}`}>
                {meta.label}
                <span className="ml-1.5 font-mono text-zinc-400">({(row.buckets[col] || []).length})</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {(row.buckets[col] || []).map((cat) => {
                  const busy = savingKey === `${row.counselling_body}|${cat}`;
                  return (
                    <span key={cat} draggable
                      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart({ body: row.counselling_body, category: cat, from: col }); }}
                      onDragEnd={onDragEnd}
                      data-testid={`open-chip-${row.counselling_body}-${cat}`}
                      className={`inline-block px-2 py-0.5 text-[11px] font-mono font-bold rounded ${meta.chip} cursor-grab active:cursor-grabbing select-none ${busy ? "opacity-40" : "hover:ring-1 hover:ring-zinc-400"}`}
                      title={`${cat} · drag to change`}>
                      {cat}
                    </span>
                  );
                })}
                {(row.buckets[col] || []).length === 0 && (
                  <span className="text-[10px] text-zinc-400 italic">empty · drop here</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
